import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  PencilSquareIcon,
  ShareIcon,
  TrashIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import MergeTeamsModal from "../components/layout/MergeTeamsModal";
import Spinner from "../components/layout/Spinner";
import Share from "../components/matches/Share";
import { isAllowedEmailUser } from "../components/utils/access";
import {
  deleteMatch,
  deleteOrphanedTeamAndPlayers,
  fetchAllMyMatches,
  fetchAllSharedMatches,
  fetchMatchViewsForMatches,
  fetchSessionById,
  removeSharedMatch,
} from "../components/utils/dbutils";
import {
  calculateDVWStats,
  generateMatch,
} from "../components/utils/DVWFile";
import { sendShareNotification } from "../components/utils/emailNotify";
import { usingFirestore } from "../components/utils/firestoreData";
import {
  calculatePSVBStats,
  initWithPSVBCompressedBuffer,
} from "../components/utils/PSVBFile";
import { fetchAllTeams } from "../components/utils/teamsPlayers";
import {
  classNames,
  dayTimeCode,
  findCommonMatchTeam,
  unzipBuffer,
} from "../components/utils/Utils";
import { myunzip } from "../components/utils/zip";
import { useLanguage } from "../context/Language/LanguageContext";
import { shareSession } from "../context/VBLiveAPI/VBLiveAPIAction";

function MatchesList({ liveMatches, userEmail }) {
  const navigate = useNavigate();
  const { firebaseUser } = useAuthStatus();
  const { t } = useLanguage();
  // Tracks which user's email we've already restored/initialized matches for.
  // `firebaseUser` (from useAuthStatus) gets a NEW object reference every time
  // Firebase refreshes the auth token (routinely, even for the SAME signed-in
  // user — e.g. on tab focus, or hourly) — without this guard, the effect
  // below (keyed on [firebaseUser]) re-fires on every such refresh and
  // restores `filteredMatches` from the last-saved localStorage snapshot,
  // silently clobbering any in-progress live state (most importantly, the
  // row checkboxes users had just checked to multi-select matches for
  // batch Delete/Share) with a stale copy that predates those selections —
  // making "select several, then Share/Delete" intermittently appear to
  // "just do nothing"/"not work" depending on exactly when a token refresh
  // happened to land.
  const lastInitEmailRef = useRef(undefined);
  // One-shot guard for the `location.state.refresh` flag set when returning
  // here after coding a (new) match — forces a single fresh DB fetch (so the
  // new match appears) instead of restoring the possibly-stale localStorage
  // snapshot, without re-fetching again on later auth-token refreshes.
  const refreshedRef = useRef(false);
  const canEdit = isAllowedEmailUser(firebaseUser);
  const location = useLocation();
  // const { liveMatches, userEmail } = location.state;
  // const { currentUser } = useContext(VBLiveAPIContext)
  // const { currentUser } = location.state;
  const [allMatches, setAllMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [allLiveMatches, setAllLiveMatches] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [allTeamOptions, setAllTeamOptions] = useState([]);
  const [selectedTeamOption, setSelectedTeamOption] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedTeamName, setSelectedTeamName] = useState("");
  const [selectedScreen, setSelectedScreen] = useState(
    () => parseInt(localStorage.getItem("matchesTab") ?? "0", 10) || 0
  );
  const [matchesList, setMatchesList] = useState(
    () => parseInt(localStorage.getItem("matchesTab") ?? "0", 10) || 0
  );
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sortColumn, setSortColumn] = useState(4);
  const [sortAscending, setSortAscending] = useState(false);
  const [thisDayTimeCode, setThisDayTimeCode] = useState(dayTimeCode());
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useState(0);
  const [viewActivity, setViewActivity] = useState([]);
  const [viewActivityLoading, setViewActivityLoading] = useState(false);
  const [matchLabels, setMatchLabels] = useState({});
  const [matchDates, setMatchDates] = useState({});
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTeamsPool, setMergeTeamsPool] = useState([]);
  const [mergeRosterWarning, setMergeRosterWarning] = useState(null);

  const doNewMatch = () => {
    navigate("/newmatch");
  };

  const doDeleteMatch = async (match) => {
    // If one or more matches are checked via the row checkboxes, delete ALL
    // of them together (mirrors `doDoShare`'s bulk behaviour below) —
    // regardless of which row's trash icon was actually clicked. With
    // nothing checked, fall back to just the single match whose icon was
    // clicked (the previous, single-match-only behaviour).
    const checked = selectedMatches();
    const matches = checked.length > 0 ? checked : [match];
    const isBulk = matches.length > 1;

    // Determine which teams would become orphans if ALL these matches were
    // removed together (a team used by one deleted match but also by
    // another deleted match must not be flagged as orphaned from a partial,
    // single-match view).
    const idsToDelete = new Set(matches.map((m) => m.id));
    const remainingMatches = allMatches.filter((m) => !idsToDelete.has(m.id));
    const teamNamesInvolved = new Set();
    matches.forEach((m) => {
      if (m.teamA) teamNamesInvolved.add(m.teamA);
      if (m.teamB) teamNamesInvolved.add(m.teamB);
    });
    const orphanedTeams = [...teamNamesInvolved].filter(
      (name) => !remainingMatches.some((m) => m.teamA === name || m.teamB === name)
    );

    // Build a sharing warning covering all matches about to be deleted.
    const publicShared = matches.filter((m) => m.shareStatus === 1);
    const userShared = matches.filter((m) => m.shareStatus === 2 && m.shareUsers);
    let shareWarning = "";
    if (publicShared.length > 0 || userShared.length > 0) {
      const parts = [];
      if (publicShared.length > 0) {
        parts.push(
          isBulk
            ? `${publicShared.length} of the selected matches ${publicShared.length > 1 ? "are" : "is"} shared publicly`
            : "This match is currently shared publicly"
        );
      }
      if (userShared.length > 0) {
        const allUsers = [
          ...new Set(
            userShared.flatMap((m) => (Array.isArray(m.shareUsers) ? m.shareUsers : [m.shareUsers]))
          ),
        ];
        parts.push(
          isBulk
            ? `${userShared.length} of the selected matches ${userShared.length > 1 ? "are" : "is"} shared with ${allUsers.join(", ")}`
            : `This match is currently shared with ${allUsers.join(", ")}`
        );
      }
      shareWarning = `⚠️ ${parts.join("; ")}. Deleting will remove that access.\n\n`;
    }

    confirmAlert({
      title: isBulk ? `Delete ${matches.length} Matches` : "Delete Match",
      message: `${shareWarning}Are you sure you want to delete ${
        isBulk ? `the ${matches.length} selected matches` : "the selected match"
      }?`,
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            setLoading(true);
            let anySucceeded = false;
            for (const m of matches) {
              const ret = await deleteMatch(m);
              if (ret) anySucceeded = true;
            }
            setLoading(false);
            if (!anySucceeded) return;
            if (orphanedTeams.length > 0) {
              const teamList = orphanedTeams.join(", ");
              const plural = orphanedTeams.length > 1;
              confirmAlert({
                title: `Delete orphaned team${plural ? "s" : ""}?`,
                message: `${teamList} ${
                  plural ? "are" : "is"
                } no longer used by any match. Delete ${
                  plural ? "these teams" : "this team"
                } and their exclusive players too?`,
                buttons: [
                  {
                    label: "Yes, delete",
                    onClick: async () => {
                      setLoading(true);
                      for (const name of orphanedTeams) {
                        await deleteOrphanedTeamAndPlayers(name);
                      }
                      setLoading(false);
                      doInit(selectedScreen);
                    },
                  },
                  {
                    label: "No, keep them",
                    onClick: () => doInit(selectedScreen),
                  },
                ],
              });
            } else {
              doInit(selectedScreen);
            }
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  // Removes from the CURRENT USER's own Shared Matches list only -- the
  // owner's match and anyone else it's shared with are unaffected.
  const doRemoveSharedMatch = async (match) => {
    const checked = selectedMatches();
    const matches = checked.length > 0 ? checked : [match];
    const isBulk = matches.length > 1;

    confirmAlert({
      title: isBulk ? `Remove ${matches.length} Shared Matches` : "Remove Shared Match",
      message: isBulk
        ? `Remove these ${matches.length} matches from your Shared Matches list? The owner and anyone else they're shared with keep their own access.`
        : "Remove this match from your Shared Matches list? The owner and anyone else it's shared with keep their own access.",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            setLoading(true);
            for (const m of matches) {
              await removeSharedMatch(m);
            }
            setLoading(false);
            doInit(selectedScreen);
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const doSelect = (sel) => () => {
    for (var m of filteredMatches) {
      m.selected = sel;
    }
    forceUpdate((n) => !n);
  };

  const doSelectTournament = (tournament, sel) => {
    for (var m of filteredMatches) {
      if (m.tournament === tournament) m.selected = sel;
    }
    forceUpdate((n) => !n);
  };

  const selectedMatches = () => {
    const ms = filteredMatches.filter((m) => m.selected && m.selected === true);
    return ms;
  };

  // Loads the full stats for the currently selected matches, validating that
  // they all belong to one common team. Returns { xms, team } or null (after
  // showing an alert) when the selection is invalid or nothing could be loaded.
  const loadSelectedMatchesForReports = async () => {
    const ms = selectedMatches();
    if (ms.length === 0) {
      alert("No matches selected.");
      return null;
    }
    setLoading(true);
    const team = findCommonMatchTeam(ms);
    if (team === null) {
      setLoading(false);
      alert("Not all selected matches are of the same team.");
      return null;
    }
    var xms = [];
    for (var m2x of ms) {
      const xm = await fetchSessionById(m2x.id);
      if (!xm || !xm.stats) {
        console.log("Skipping match with no stats", m2x.id);
        continue;
      }
      var buffer = myunzip(xm.stats);
      if (!buffer) {
        buffer = unzipBuffer(xm.stats);
      }
      xm.buffer = buffer;
      xms.push(xm);
    }
    setLoading(false);
    if (xms.length === 0) {
      alert("Could not load stats for the selected matches.");
      return null;
    }
    return { xms, team };
  };

  const doMergeTeams = async () => {
    const ms = selectedMatches();
    // Collect all unique team names from the selected matches
    const teamNames = new Set();
    for (const m of ms) {
      if (m.teamA) teamNames.add(m.teamA);
      if (m.teamB) teamNames.add(m.teamB);
    }
    setLoading(true);
    let allTs = [];
    try {
      allTs = await fetchAllTeams();
    } catch (e) {
      console.warn("doMergeTeams: fetchAllTeams failed", e);
    }
    setLoading(false);
    // Filter to teams whose name matches those in the selected matches
    const pool = allTs.filter((tm) => teamNames.has(tm.name));
    if (pool.length < 2) {
      alert(t("At least 2 team records must exist in your Teams list for the selected matches."));
      return;
    }
    // Compare rosters: warn if player count or shirt numbers differ
    const rosterData = pool.map((tm) => ({
      name: tm.name,
      count: (tm.players ?? []).length,
      shirts: (tm.players ?? [])
        .map((p) => String(p.shirtNumber ?? "").trim())
        .filter(Boolean)
        .sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0)),
    }));
    const ref = rosterData[0];
    const diffTeams = rosterData.slice(1).filter(
      (r) =>
        r.count !== ref.count ||
        r.shirts.join(",") !== ref.shirts.join(",")
    );
    const warning = diffTeams.length > 0 ? rosterData : null;
    setMergeTeamsPool(pool);
    setMergeRosterWarning(warning);
    setShowMergeModal(true);
  };

  const doMultiMatchReports = async () => {
    const res = await loadSelectedMatchesForReports();
    if (!res) {
      return;
    }
    navigate("/multisessions", { state: { matches: res.xms, team: res.team } });
  };

  const doAIReports = async () => {
    const res = await loadSelectedMatchesForReports();
    if (!res) {
      return;
    }
    navigate("/aireports", { state: { matches: res.xms, team: res.team } });
  };

  const doShareMatch = (match) => {
    setThisDayTimeCode(dayTimeCode());
    setSelectedMatch(match);
    setShowShareModal(true);
  };

  const doDoShare = async (share) => {
    setShowShareModal(false);
    if (!share) {
      return;
    }
    // Batch-share every CHECKED row (regardless of which row's icon opened
    // this modal), falling back to just the row whose icon was clicked when
    // nothing is checked. Each `shareSession` call is awaited and wrapped
    // individually so one failure (e.g. a transient network/API error)
    // doesn't silently abort the remaining matches or leave the user with
    // no indication anything went wrong — previously a failure partway
    // through the (unguarded) loop, or an exception thrown by the `!ok`
    // fallback branch referencing a stale/null `selectedMatch`, could abort
    // before `forceUpdate`, making a multi-select Share look like it "did
    // nothing".
    const checked = filteredMatches.filter((m) => m.selected === true);
    const targets = checked.length > 0 ? checked : selectedMatch ? [selectedMatch] : [];
    if (targets.length === 0) return;
    setLoading(true);
    let successCount = 0;
    for (const m of targets) {
      m.shareStatus = share.shareStatus;
      m.shareUsers = share.shareUsers;
      try {
        const ret = await shareSession(m);
        if (ret) successCount++;
        else
          console.warn(
            "doDoShare: shareSession returned falsy for match",
            m.id,
            "usingFirestore:", usingFirestore(),
            "shareStatus:", m.shareStatus,
          );
      } catch (e) {
        console.error("doDoShare: shareSession threw for match", m.id, e);
      }
    }
    setLoading(false);
    if (successCount < targets.length) {
      alert(
        targets.length > 1
          ? `Shared ${successCount} of ${targets.length} matches. Some matches could not be shared — please try again.`
          : "Could not share the match. Please try again."
      );
    }
    if (share.notifyByEmail && share.shareStatus === 2 && successCount > 0) {
      const recipients = Array.isArray(share.shareUsers)
        ? share.shareUsers
        : String(share.shareUsers ?? "").split(",");
      sendShareNotification({
        to: recipients,
        itemType: "match",
        itemTitle:
          targets.length > 1
            ? `${targets.length} matches`
            : `${targets[0]?.teamA ?? ""} vs ${targets[0]?.teamB ?? ""}`,
        sharedByEmail: userEmail,
      });
    }
    forceUpdate((n) => !n);
  };

  const doMatch = async (match) => {
    const session = await fetchSessionById(match.id);
    if (!session || !session.stats) {
      alert(t("Could not load match data. The match may no longer be available."));
      return;
    }
    var uz = myunzip(session.stats);
    if (!uz || uz.length === 0) {
      uz = unzipBuffer(session.stats);
    }
    const dvbuf = uz.includes("PSVB") ? null : uz;
    const vbbuf = uz.includes("PSVB") ? uz : null;
    const st = {
      msession: session,
      dvwFileData: dvbuf,
      psvbFileData: vbbuf,
      filename: "",
    };
    navigate("/session", { state: st });
  };

  const doEditMatch = async (match) => {
    setLoading(true);
    const session = await fetchSessionById(match.id);
    if (!session || !session.stats) {
      setLoading(false);
      alert(t("Could not load match data. The match may no longer be available."));
      return;
    }
    let buffer = myunzip(session.stats);
    if (!buffer || buffer.length === 0) {
      buffer = unzipBuffer(session.stats);
    }
    const isPSVB = buffer?.includes("PSVB");
    let parsedMatch = null;
    if (isPSVB) {
      const m = initWithPSVBCompressedBuffer(session.stats);
      parsedMatch = calculatePSVBStats(m);
      parsedMatch.app = "VBStats";
    } else {
      const m = generateMatch(buffer);
      parsedMatch = calculateDVWStats(m);
      parsedMatch.app = "DataVolley";
    }
    parsedMatch.videoOnlineUrl = session.videoOnlineUrl ?? match.videoOnlineUrl ?? null;
    parsedMatch.videoStartTimeSeconds =
      session.videoStartTimeSeconds ?? match.videoStartTimeSeconds ?? -1;
    parsedMatch.videoOffset = session.videoOffset ?? match.videoOffset ?? -1;
    // Restore the live-coding state that the DVW buffer can't carry — the
    // chosen match squad + substitution-pair tracking — so resuming the match
    // rebuilds the sub popover / history correctly.
    parsedMatch.matchSquadA = session.matchSquadA ?? null;
    parsedMatch.matchSquadB = session.matchSquadB ?? null;
    parsedMatch.subPairs = session.subPairs ?? null;

    const st = {
      teamA: {
        ...(parsedMatch.teamA ?? {}),
        colour: parsedMatch.teamA?.colour ?? "lime",
      },
      teamB: {
        ...(parsedMatch.teamB ?? {}),
        colour: parsedMatch.teamB?.colour ?? "magenta",
      },
      match: parsedMatch,
    };
    setLoading(false);
    navigate("/dvmatchcoding", { state: st });
  };

  const doSort = (aschs, sc, sa) => {
    var xx = null;
    if (sc === 0) {
      xx = sa
        ? aschs.sort((a, b) => a.teamA.localeCompare(b.teamA))
        : aschs.sort((a, b) => b.teamA.localeCompare(a.teamA));
    } else if (sc === 1) {
      xx = sa
        ? aschs.sort((a, b) => a.teamB.localeCompare(b.teamB))
        : aschs.sort((a, b) => b.teamB.localeCompare(a.teamB));
    } else if (sc === 2) {
      xx = sa
        ? aschs.sort((a, b) => a.tournament.localeCompare(b.tournament))
        : aschs.sort((a, b) => b.tournament.localeCompare(a.tournament));
    } else if (sc === 4) {
      xx = sa
        ? aschs.sort(
            (a, b) => a.sessionDateTimeInSeconds - b.sessionDateTimeInSeconds
          )
        : aschs.sort(
            (a, b) => b.sessionDateTimeInSeconds - a.sessionDateTimeInSeconds
          );
    } else if (sc === 5) {
      xx = sa
        ? aschs.sort((a, b) => (a.ownerEmail ?? "").localeCompare(b.ownerEmail ?? ""))
        : aschs.sort((a, b) => (b.ownerEmail ?? "").localeCompare(a.ownerEmail ?? ""));
    } else {
      return;
    }

    setFilteredMatches(xx);
    setSortColumn(sc);
    setSortAscending(sa);
    forceUpdate((n) => !n);
  };

  const doToggleSelect = (match) => {
    match.selected = !match.selected;
    forceUpdate((n) => !n);
  };

  const columnHeader = (col, name) => {
    return (
      <th
        onClick={() => {
          var asc = sortColumn === col ? !sortAscending : true;
          doSort(filteredMatches, col, asc);
        }}
        scope="col"
        className="table-header-column text-sm p-2"
      >
        <div className="flex">
          <p>{t(name)}</p>
          {sortColumn === col ? (
            <div>
              {sortAscending ? (
                <ArrowDownIcon
                  className="ml-1 mt-0.5 h-4 w-4 flex-shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <ArrowUpIcon
                  className="ml-1 mt-0.5 h-4 w-4 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
            </div>
          ) : (
            <></>
          )}
        </div>
      </th>
    );
  };

  const onTeamChanged = (e) => {
    const team =
      e.target.selectedIndex > 0 ? allTeams[e.target.selectedIndex - 1] : null;
    let fms = [];
    setSelectedTeamOption(team && team.name);
    setSearchText("");
    // Key-case bug: this used to write "SearchText" (capitalised) while every
    // other read/write in this file uses "searchText" — the no-op left stale
    // search text in storage, which silently re-filtered (and could hide)
    // matches the next time the list was loaded, even after switching to the
    // team dropdown.
    localStorage.setItem("searchText", "");
    if (!team) {
      localStorage.setItem("selectedTeamName", "");
      setSelectedTeamName(null);
      setSelectedTeam(null);
      fms = allMatches;
    } else {
      localStorage.setItem("selectedTeamName", team.name);
      setSelectedTeamName(team.name);
      setSelectedTeam(team);
      fms = allMatches.filter(
        (m) => m.teamA === team.name || m.teamB === team.name
      );
    }
    setFilteredMatches(fms);
    saveLiveMatches({
      serverName: "Live Matches",
      allMatches: allMatches,
      filteredMatches: fms,
      allTeams: allTeams,
      allTeamOptions: allTeamOptions,
      selectedTeamOption: team && team.name,
      selectedTeamName: team ? team.name : "",
      selectedTeam: team,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      searchText: "",
      selectedScreen: selectedScreen,
      userEmail: firebaseUser?.email,
    });
    forceUpdate((n) => !n);
  };

  const doSeachTextChanged = (e) => {
    const search = e.target.value.toLowerCase();
    setSearchText(search);
    setSelectedTeamName(null);
    setSelectedTeam(null);
    setSelectedTeamOption(null);
    localStorage.setItem("searchText", search);
    localStorage.setItem("selectedTeamName", "");
    const fms = allMatches.filter(
      (m) =>
        m.teamA.toLowerCase().includes(search) ||
        m.teamB.toLowerCase().includes(search) ||
        m.tournament.toLowerCase().includes(search)
    );
    setFilteredMatches(fms);
    saveLiveMatches({
      serverName: "Live Matches",
      allMatches: allMatches,
      filteredMatches: fms,
      allTeams: allTeams,
      allTeamOptions: allTeamOptions,
      selectedTeamOption: null,
      selectedTeamName: null,
      selectedTeam: null,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      searchText: search,
      selectedScreen: selectedScreen,
      userEmail: firebaseUser?.email,
    });
    forceUpdate((n) => !n);
  };

  const formatOptionLabel = ({ value, label }) => (
    <div className="flex gap-2">
      {/* <div className="-mt-0.5">
        {value ? getFlag(value.countrycode, 7, 5) : <></>}
      </div> */}
      <div className="font-medium text-black">{label}</div>
    </div>
  );

  // Cache the matches list in localStorage, tolerating quota overflow.
  // Shared-match payloads can exceed the ~5MB limit; on failure we drop the
  // cache (so the next visit re-fetches) instead of throwing.
  const saveLiveMatches = (data) => {
    try {
      localStorage.setItem("liveMatches", JSON.stringify(data));
    } catch (e) {
      try {
        localStorage.removeItem("liveMatches");
      } catch (_) {
        /* ignore */
      }
      console.warn("liveMatches cache skipped (storage quota):", e?.message ?? e);
    }
  };

  const doInit = async (selscreen) => {
    // const storedlist = localStorage.getItem("liveMatches");
    // if (storedlist) {
    //   const liveMatches = JSON.parse(storedlist);
    //   // if (liveMatches.selectedScreen === selectedScreen) {
    //     setFilteredMatches(liveMatches.filteredMatches);
    //     setAllMatches(liveMatches.allMatches);
    //     setAllTeams(liveMatches.allTeams);
    //     setAllTeamOptions(liveMatches.allTeamOptions);
    //     setSelectedTeamOption(liveMatches.selectedTeamOption);
    //     setSelectedTeamName(liveMatches.selectedTeamName);
    //     setSelectedTeam(liveMatches.selectedTeam);
    //     setSortColumn(liveMatches.sortColumn);
    //     setSortAscending(liveMatches.sortAscending);
    //     setSearchText(liveMatches.searchText);
    //     setSelectedScreen(liveMatches.selectedScreen);
    //     setLoading(false);
    //     forceUpdate((n) => !n);
    //     return;
    //   // }
    // }
    if (selscreen !== undefined && selscreen !== null) {
      setMatchesList(selscreen);
    } else {
      selscreen = matchesList;
    }
    var ms = [];
    if (liveMatches && liveMatches.length > 0) {
      for (var m of liveMatches) {
        if (!m.teamA || !m.teamB) {
          const tokens = m.description.split(" vs ");
          if (tokens.length === 2) {
            m.teamA = tokens[0];
            m.teamB = tokens[1];
          } else {
            m.teamA = "";
            m.teamB = "";
          }
        }
        ms.push(m);
      }
      setAllLiveMatches(ms);
      setSelectedScreen(2);
      setLoading(false);
    } else if (firebaseUser) {
      setLoading(true);
      ms =
        selscreen === 0
          ? await fetchAllMyMatches(firebaseUser.email)
          : await fetchAllSharedMatches(firebaseUser.email);
      setLoading(false);
    }
    var pls = {};
    for (var m of ms) {
      if (!pls[m.teamA]) {
        pls[m.teamA] = {
          name: m.teamA,
        };
      }
      if (!pls[m.teamB]) {
        pls[m.teamB] = {
          name: m.teamB,
        };
      }
    }
    var ps = [];
    for (var key in pls) {
      ps.push(pls[key]);
    }
    const aps = ps.sort((a, b) => a.name.localeCompare(b.name));
    setAllTeams(aps);
    var apops = [{ value: null, label: "All Teams" }];
    for (var p of aps) {
      // apops.push({ value: p, label: p.name });
      apops.push(p);
    }
    setAllTeamOptions(apops);
    doSort(ms, sortColumn, sortAscending);
    setAllMatches(ms);

    const search = localStorage.getItem("searchText");
    if (search && search.length > 0) {
      setSearchText(search);
      const fms = ms.filter(
        (m) =>
          m.teamA.toLowerCase().includes(search) ||
          m.teamB.toLowerCase().includes(search) ||
          m.tournament.toLowerCase().includes(search)
      );
      setFilteredMatches(fms);
    } else if (firebaseUser) {
      const teamname = localStorage.getItem("selectedTeamName");
      if (teamname && teamname.length > 0) {
        const poption = apops.find((p) => p.name === teamname);
        setSelectedTeamOption(poption);
        const team = aps.find((p) => p.name === teamname);
        if (team) {
          setSelectedTeamName(team.name);
          setSelectedTeam(team);
          const fms = ms.filter(
            (m) => m.teamA === team.name || m.teamB === team.name
          );
          setFilteredMatches(fms);
        }
      } else {
        setFilteredMatches(ms);
      }
    }
    saveLiveMatches({
      serverName: "Live Matches",
      allMatches: ms,
      filteredMatches: ms,
      allTeams: aps,
      allTeamOptions: apops,
      selectedTeamOption: null,
      selectedTeamName: null,
      selectedTeam: null,
      sortColumn: sortColumn,
      sortAscending: sortAscending,
      searchText: searchText,
      selectedScreen: selscreen,
      userEmail: firebaseUser?.email,
    });
  };

  useEffect(() => {
    // Returning here after coding a (new) match: force a fresh DB fetch so it
    // appears in the list, skipping the localStorage-snapshot restore below.
    // Wait until `firebaseUser` is available; the [firebaseUser] effect retries.
    if (location.state?.refresh && !refreshedRef.current) {
      if (firebaseUser) {
        refreshedRef.current = true;
        lastInitEmailRef.current = firebaseUser?.email ?? null;
        doInit(selectedScreen);
      }
      return;
    }
    const storedlist = localStorage.getItem("liveMatches");
    if (storedlist) {
      const liveMatches = JSON.parse(storedlist);
      if (liveMatches.userEmail === firebaseUser?.email) {
        setFilteredMatches(liveMatches.filteredMatches);
        setAllMatches(liveMatches.allMatches);
        setAllTeams(liveMatches.allTeams);
        setAllTeamOptions(liveMatches.allTeamOptions);
        setSelectedTeamOption(liveMatches.selectedTeamOption);
        setSelectedTeamName(liveMatches.selectedTeamName);
        setSelectedTeam(liveMatches.selectedTeam);
        setSortColumn(liveMatches.sortColumn);
        setSortAscending(liveMatches.sortAscending);
        setSearchText(liveMatches.searchText);
        setSelectedScreen(liveMatches.selectedScreen);
        setLoading(false);
        forceUpdate((n) => !n);
      } else if (firebaseUser) {
        doInit(selectedScreen);
      }
    } else {
      doInit(selectedScreen);
    }
    lastInitEmailRef.current = firebaseUser?.email ?? null;
  }, []);

  useEffect(() => {
    // Skip re-running this restore-or-reload logic when we've already
    // initialized for this EXACT email — `firebaseUser` gets a new object
    // identity on every Firebase token refresh even without a real user
    // change, and re-running would clobber in-progress live state (e.g.
    // checked row checkboxes) with a stale localStorage snapshot. See the
    // `lastInitEmailRef` comment above for the full rationale.
    if (lastInitEmailRef.current === (firebaseUser?.email ?? null)) return;
    lastInitEmailRef.current = firebaseUser?.email ?? null;
    // Same forced-refresh handling as the mount effect, in case `firebaseUser`
    // only became available after mount.
    if (location.state?.refresh && !refreshedRef.current) {
      if (firebaseUser) {
        refreshedRef.current = true;
        doInit(selectedScreen);
      }
      return;
    }
    const storedlist = localStorage.getItem("liveMatches");
    if (storedlist) {
      const liveMatches = JSON.parse(storedlist);
      if (liveMatches.userEmail === firebaseUser?.email) {
        setFilteredMatches(liveMatches.filteredMatches);
        setAllMatches(liveMatches.allMatches);
        setAllTeams(liveMatches.allTeams);
        setAllTeamOptions(liveMatches.allTeamOptions);
        setSelectedTeamOption(liveMatches.selectedTeamOption);
        setSelectedTeamName(liveMatches.selectedTeamName);
        setSelectedTeam(liveMatches.selectedTeam);
        setSortColumn(liveMatches.sortColumn);
        setSortAscending(liveMatches.sortAscending);
        setSearchText(liveMatches.searchText);
        setSelectedScreen(liveMatches.selectedScreen);
        setLoading(false);
        forceUpdate((n) => !n);
      } else if (firebaseUser) {
        doInit(selectedScreen);
      }
    } else {
      doInit(selectedScreen);
    }
  }, [firebaseUser]);

  const makeDate = (seconds) => {
    const dt = new Date(seconds * 1000);
    return dt.toLocaleDateString();
  };

  const getSharedColour = (match) => {
    if (match.shareStatus === undefined || match.shareStatus === null) {
      if (match.shareStatus === 1) {
        return "mr-1.5 h-5 w-5 flex-shrink-0 text-success";
      } else {
        return "mr-1.5 h-5 w-5 flex-shrink-0 text-warning";
      }
    } else {
      if (match.shareStatus === 1) {
        return "mr-1.5 h-5 w-5 flex-shrink-0 text-success";
      } else if (match.shareStatus === 2) {
        return "mr-1.5 h-5 w-5 flex-shrink-0 text-warning";
      } else {
        return "mr-1.5 h-5 w-5 flex-shrink-0 text-error";
      }
    }
  };

  // const getFlag = (countrycode) => {
  //   if (countrycode) {
  //     return (
  //       <img
  //         className="pl-2 w-7 h-5"
  //         alt=""
  //         src={require(`../assets/flags/${countrycode}.png`)}
  //       />
  //     );
  //   } else {
  //     return <></>;
  //   }
  // };

  const doLoadViewActivity = async () => {
    setViewActivityLoading(true);
    try {
      const matches = await fetchAllMyMatches(firebaseUser?.email);
      const sharedMatches = matches.filter((m) => m.shareStatus && m.shareStatus > 0);
      const labels = {};
      const dates = {};
      for (const m of sharedMatches) {
        labels[m.id] = `${m.teamA} vs ${m.teamB}`;
        dates[m.id] = m.sessionDateTimeInSeconds ?? 0;
      }
      setMatchLabels(labels);
      setMatchDates(dates);
      const ids = sharedMatches.map((m) => m.id);
      const views = await fetchMatchViewsForMatches(ids);
      setViewActivity(views);
    } catch (e) {
      console.warn("doLoadViewActivity:", e);
    }
    setViewActivityLoading(false);
  };

  const onMatchesScreenChanged = async (screen) => {
    localStorage.setItem("matchesTab", String(screen));
    setSelectedScreen(screen);
    if (screen === 2) {
      doLoadViewActivity();
    } else {
      doInit(screen);
    }
  };

  const customStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      // minHeight: '32px',
      // height: '32px',
      borderRadius: 4,
      color: "blue",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderColor: state.isFocused ? "oklch(var(--p))" : "transparent",
      ":hover": {
        borderColor: "transparent",
      },
    }),
    valueContainer: (baseStyles, state) => ({
      ...baseStyles,
      padding: "0 8px",
      // backgroundColor: "oklch(var(--p))",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
    }),
    // input: (baseStyles, state) => ({
    //   ...baseStyles,
    //   color: "oklch(var(--bc))",
    // }),
    // singleValueLabel: (baseStyles, state) => ({
    //   ...baseStyles,
    //   color: "oklch(var(--bc))",
    // }),
    // multiValue: (baseStyles, state) => ({
    //   ...baseStyles,
    //   backgroundColor: "oklch(var(--b2))",
    // }),
    // multiValueLabel: (baseStyles, state) => ({
    //   ...baseStyles,
    //   color: "oklch(var(--bc))",
    // }),
    // multiValueRemove: (baseStyles, state) => ({
    //   ...baseStyles,
    //   color: "oklch(var(--bc))",
    //   ":hover": {
    //     backgroundColor: "rgba(255, 255, 255, 0.2)",
    //     color: "oklch(var(--bc))",
    //   },
    // }),
  };

  const tabs = [
    { name: "My Matches", index: 0, current: true },
    { name: "Shared Matches", index: 1, current: false },
    { name: "View Activity", index: 2, current: false },
  ];

  return (
    <>
      <div className="flex-col h-[88vh] mt-4">
          {/* <div className="flex gap-2 my-2 text-sm">
          {functionTabSecondary(
            selectedScreen === 0,
            0,
            "My Matches",
            onMatchesScreenChanged
          )}
          {functionTabSecondary(
            selectedScreen === 1,
            1,
            "Shared Matches",
            onMatchesScreenChanged
          )}
          {allLiveMatches ? (
            <>
              {functionTabSecondary(
                selectedScreen === 2,
                1,
                "Live Matches",
                onMatchesScreenChanged
              )}
            </>
          ) : (
            <></>
          )}
        </div> */}
          <div className="border-b border-gray-200 mb-4">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <a
                  key={tab.name}
                  onClick={() => onMatchesScreenChanged(tab.index)}
                  aria-current={
                    tab.index === selectedScreen ? "page" : undefined
                  }
                  className={classNames(
                    tab.index === selectedScreen
                      ? "border-primary/80 text-primary/80"
                      : "border-transparent text-base-content hover:border-base-content/30 hover:text-base-content/70",
                    "whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium"
                  )}
                >
                  {t(tab.name)}
                </a>
              ))}
            </nav>
          </div>

          <div className="grid grid-cols-6 gap-x-2 gap-y-2">
            <div className="col-span-3">
              <input
                type="text"
                value={searchText}
                className="input-generic"
                placeholder={t("Search team...")}
                onChange={(e) => {
                  doSeachTextChanged(e);
                }}
              />
            </div>
            <div className="col-span-3">
              <div className="flex gap-2">
                <select
                  className="select-generic"
                  onChange={onTeamChanged}
                  value={selectedTeamName}
                >
                  <option value="all">{t("All Teams")}</option>
                  {allTeams.map((team, i) => (
                    <option key={i} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
                {canEdit && (
                  <button className="btn-in-form" onClick={() => doNewMatch()}>
                    {t("New")}
                  </button>
                )}
                <button className="btn-in-form" onClick={() => doInit()}>
                  {t("Refresh")}
                </button>
              </div>
            </div>
          </div>

          {(searchText || selectedTeamName) && (
            <div className="alert alert-info py-1 px-3 mt-2 text-xs flex justify-between">
              <span>
                {t("Filtered by")}: {searchText ? `"${searchText}"` : selectedTeamName}
                {" — "}
                {t("showing")} {filteredMatches.length} {t("of")} {allMatches.length}
              </span>
              <button
                className="underline"
                onClick={() => {
                  setSearchText("");
                  setSelectedTeamName(null);
                  setSelectedTeam(null);
                  setSelectedTeamOption(null);
                  localStorage.setItem("searchText", "");
                  localStorage.setItem("selectedTeamName", "");
                  setFilteredMatches(allMatches);
                }}
              >
                {t("Clear filter")}
              </button>
            </div>
          )}

          {selectedScreen !== 2 && (
          <div className="flex justify-between mt-2">
            <div className="flex gap-2">
              <div className="tooltip" data-tip={t("Select All")}>
                <div className="btn-in-form w-10" onClick={doSelect(true)}>
                  <CheckIcon
                    className="w-6 h-6"
                    style={{ color: "oklch(var(--bc))" }}
                  />

                  {/* <img src={selectall} alt="Select All" className="w-6 h-6" /> */}
                </div>
              </div>
              <div className="tooltip" data-tip={t("Unselect All")}>
                <div className="btn-in-form w-10" onClick={doSelect(false)}>
                  <XMarkIcon
                    className="w-6 h-6"
                    style={{ color: "oklch(var(--bc))" }}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <button
                  disabled={selectedMatches().length < 1}
                  className="btn-in-form"
                  onClick={() => doAIReports()}
                >
                  {t("AI Reports")}
                </button>
              )}
              <button
                disabled={selectedMatches().length < 2}
                className="btn-in-form"
                onClick={() => doMultiMatchReports()}
              >
                {t("Multi Match Reports")}
              </button>
              {canEdit && usingFirestore() && (
                <button
                  disabled={selectedMatches().length < 2}
                  className="btn-in-form"
                  onClick={() => doMergeTeams()}
                >
                  {t("Merge Teams")}
                </button>
              )}
            </div>
          </div>
          )}
          {selectedScreen === 2 ? (
            viewActivityLoading ? (
              <Spinner />
            ) : (
              <>
                <div className="flex items-center justify-between mt-2 mb-1">
                  <span className="text-sm text-base-content/60">{viewActivity.length} view{viewActivity.length !== 1 ? "s" : ""}</span>
                  <button className="btn-in-form" onClick={doLoadViewActivity}>{t("Refresh")}</button>
                </div>
                <div className="flex-col mt-1 rounded-md">
                  <div className="overflow-y-auto overflow-x-scroll h-[70vh]">
                    <div className="inline-block min-w-full align-middle">
                      <table className="table-generic">
                        <thead className="thead-generic">
                          <tr>
                            <th className="table-header-column text-sm p-2">{t("Viewer")}</th>
                            <th className="table-header-column text-sm p-2">{t("Date / Time")}</th>
                          </tr>
                        </thead>
                        <tbody className="tbody-generic">
                          {(() => {
                            // Group views by matchId, preserving order of first appearance
                            const groups = [];
                            const groupMap = {};
                            for (const v of viewActivity) {
                              if (!groupMap[v.matchId]) {
                                groupMap[v.matchId] = [];
                                groups.push(v.matchId);
                              }
                              groupMap[v.matchId].push(v);
                            }
                            // Sort groups by match date descending (newest match first)
                            groups.sort((a, b) => (matchDates[b] ?? 0) - (matchDates[a] ?? 0));
                            if (groups.length === 0) {
                              return (
                                <tr><td colSpan={2} className="table-cell text-center text-base-content/50 py-8">{t("No views recorded yet.")}</td></tr>
                              );
                            }
                            let rowIdx = 0;
                            return groups.map((matchId) => {
                              const rows = groupMap[matchId];
                              return (
                                <Fragment key={matchId}>
                                  <tr>
                                    <td colSpan={2} className="bg-base-300/50 px-3 py-1 text-xs font-semibold text-base-content/70 uppercase tracking-wider">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          {matchLabels[matchId] ?? matchId}
                                          <span className="ml-2 font-normal normal-case text-base-content/50">{rows.length} view{rows.length !== 1 ? "s" : ""}</span>
                                        </div>
                                        <span className="font-normal normal-case text-base-content/50">
                                          {matchDates[matchId] ? makeDate(matchDates[matchId]) : ""}
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                  {rows.map((v, i) => {
                                    const stripe = rowIdx++ % 2 === 0;
                                    return (
                                      <tr key={i} className={stripe ? "bg-transparent" : "bg-base-300/10"}>
                                        <td className="table-cell text-sm">{v.viewerEmail}</td>
                                        <td className="table-cell text-sm">{v.viewedAt ? v.viewedAt.toLocaleString() : "—"}</td>
                                      </tr>
                                    );
                                  })}
                                </Fragment>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )
          ) : loading ? (
            <Spinner />
          ) : (
            <>
              <div className="flex-col mt-2 rounded-md">
                <div className="overflow-y-auto overflow-x-scroll h-[70vh]">
                  <div className="inline-block min-w-full align-middle">
                    <div className="">
                      <table className="table-generic">
                        <thead className="thead-generic">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 text-left text-sm font-semibold text-base-content"
                            ></th>
                            {columnHeader(0, "Teams")}
                            {columnHeader(4, "Date / Scores")}
                            {columnHeader(2, "Tournament")}
                            {selectedScreen === 1 && columnHeader(5, "Shared By")}
                            <th
                              scope="col"
                              className="sticky right-0 bg-neutral/50 px-3 py-2 text-right text-sm font-semibold text-base-content"
                            >
                              {t("Actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="tbody-generic">
                          {filteredMatches &&
                            filteredMatches.map((match, i) => (
                              <Fragment key={i}>
                                {sortColumn === 2 && (i === 0 || match.tournament !== filteredMatches[i - 1].tournament) && (
                                  <tr>
                                    <td colSpan={selectedScreen === 1 ? 6 : 5} className="bg-base-300/50 px-3 py-1 text-xs font-semibold text-base-content/60 uppercase tracking-wider">
                                      <div className="flex items-center gap-2">
                                        {(() => {
                                          const gm = filteredMatches.filter(m => m.tournament === match.tournament);
                                          const allSel = gm.length > 0 && gm.every(m => m.selected);
                                          return (
                                            <input
                                              type="checkbox"
                                              checked={allSel}
                                              className="checkbox checkbox-xs rounded-none"
                                              onChange={() => doSelectTournament(match.tournament, !allSel)}
                                            />
                                          );
                                        })()}
                                        {match.tournament || "—"}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                <tr
                                  className={
                                    i % 2 === 0
                                      ? "bg-transparent"
                                      : "bg-base-300/10"
                                  }
                                >
                                <td className="table-cell">
                                  <input
                                    type="checkbox"
                                    checked={
                                      match.selected && match.selected === true
                                    }
                                    className="checkbox checkbox-sm mt-1 rounded-none"
                                    onChange={() => doToggleSelect(match)}
                                  />
                                </td>
                                <td className="table-cell">
                                  {(() => {
                                    const sets = match.scores ? match.scores.split(", ") : [];
                                    const aW = sets.filter(s => { const p = s.split("-").map(Number); return p.length === 2 && p[0] > p[1]; }).length;
                                    const bW = sets.filter(s => { const p = s.split("-").map(Number); return p.length === 2 && p[1] > p[0]; }).length;
                                    const teamAWon = sets.length > 0 && aW > bW;
                                    const teamBWon = sets.length > 0 && bW > aW;
                                    return (
                                      <div className="flex flex-col gap-0.5 cursor-pointer" onClick={() => doMatch(match)}>
                                        <div className={`underline${teamAWon ? " font-bold" : ""}`}>{match.teamA}</div>
                                        <div className={`underline text-base-content/70${teamBWon ? " font-bold" : ""}`}>{match.teamB}</div>
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="table-cell">
                                  <div className="flex flex-col gap-0.5">
                                    <div>{makeDate(match.sessionDateTimeInSeconds)}</div>
                                    <div className="text-base-content/60">{match.scores}</div>
                                  </div>
                                </td>
                                <td className="table-cell">
                                  {match.tournament}
                                </td>
                                {selectedScreen === 1 && (
                                  <td className="table-cell text-sm text-base-content/70">
                                    {match.ownerEmail || "—"}
                                  </td>
                                )}
                                <td
                                  className={classNames(
                                    "table-cell sticky right-0",
                                    i % 2 === 0 ? "bg-base-100" : "bg-base-200"
                                  )}
                                >
                                  <div className="flex items-center justify-end gap-1">
                                    {selectedScreen === 0 ? (
                                      <>
                                        <TrashIcon
                                          className="h-5 w-5 flex-shrink-0 cursor-pointer text-base-content"
                                          aria-hidden="true"
                                          title={t("Delete match")}
                                          onClick={() => doDeleteMatch(match)}
                                        />
                                        <ShareIcon
                                          className={
                                            getSharedColour(match) +
                                            " cursor-pointer"
                                          }
                                          aria-hidden="true"
                                          title={t("Share match")}
                                          onClick={() => doShareMatch(match)}
                                        />
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                    {selectedScreen === 1 && (
                                      <XMarkIcon
                                        className="h-5 w-5 flex-shrink-0 cursor-pointer text-base-content"
                                        aria-hidden="true"
                                        title={t("Remove from my shared matches")}
                                        onClick={() => doRemoveSharedMatch(match)}
                                      />
                                    )}
                                    {canEdit && (
                                      <PencilSquareIcon
                                        className="h-5 w-5 flex-shrink-0 cursor-pointer text-base-content"
                                        aria-hidden="true"
                                        title={t("Edit match")}
                                        onClick={() => doEditMatch(match)}
                                      />
                                    )}
                                    <VideoCameraIcon
                                      className={
                                        match.videoOnlineUrl
                                          ? "h-5 w-5 flex-shrink-0 cursor-pointer text-success"
                                          : "h-5 w-5 flex-shrink-0 cursor-pointer text-base-content"
                                      }
                                      aria-hidden="true"
                                      title={t("Open video analysis")}
                                      onClick={() => doMatch(match)}
                                    />
                                  </div>
                                </td>
                                </tr>
                              </Fragment>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      {showMergeModal && (
        <MergeTeamsModal
          allTeams={mergeTeamsPool}
          rosterWarning={mergeRosterWarning}
          preSelectedIds={new Set(mergeTeamsPool.map((tm) => tm.id))}
          onClose={() => setShowMergeModal(false)}
          onDone={() => {
            setShowMergeModal(false);
            doInit();
          }}
          t={t}
        />
      )}
      {showShareModal && (
        <dialog
          className="modal z-[9999]"
          open
          onKeyDown={(e) => { if (e.key === "Escape") setShowShareModal(false); }}
        >
          <div className="modal-box sm:w-4/12 w-full max-w-5xl min-w-[480px] h-[70vh] shadow rounded-none">
            <h3 className="mb-4 font-bold text-2xl">
              {selectedMatches().length > 1
                ? `${t("Share")} (${selectedMatches().length} ${t("matches")})`
                : t("Share")}
            </h3>
            <div className="flex flex-col">
              <div>
                <Share
                  shareStatus={selectedMatch && selectedMatch.shareStatus}
                  shareUsers={selectedMatch && selectedMatch.shareUsers}
                  currentTime={thisDayTimeCode}
                  matchId={selectedMatch?.id}
                  onShare={(share) => doDoShare(share)}
                />
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowShareModal(false)} />
        </dialog>
      )}
    </>
  );
}

export default MatchesList;
